import { faSparkle } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import SeasonsPerIngredient from 'classes/Recipe/Detail/Calculation/SeasonsPerIngredient';
import { useStyles } from 'components/desktop/Recipe/RecipeTabs/SeasonalityView/SeasonalityView.tss';
import SeasonalityBarBig from 'components/desktop/_general/SeasonalityBar/SeasonalityBarBig';
import SeasonsHistogramLarge from 'components/desktop/_general/SeasonalityHistogram/seasonalityHistogramLarge';
import { IncorporationShareType } from 'types/Recipe/IngredientIncorporationShare';
import { relevanceType, seasonsHistogram } from 'types/Recipe/Recipe';
import { Segment } from 'types/Recipe/Segment';
import { SegmentIngredient } from 'types/Recipe/SegmentIngredient';

interface IProps {
	segments: Segment[] | undefined;
	seasonsPerIngredient: SeasonsPerIngredient[];
	incorporationShares: IncorporationShareType[];
	seasonsHistogram: seasonsHistogram;
}

const getMonthAvailability = (seasonalityCode: number) => {
	const binaryNumber = seasonalityCode.toString(2).padStart(12, '0').split('').reverse();
	return binaryNumber.map((bit) => bit === '1');
};

const SeasonalityView = ({
	segments,
	seasonsPerIngredient,
	incorporationShares,
	seasonsHistogram,
}: IProps) => {
	const { t } = useTranslation();
	const { classes } = useStyles();

	function getRelevanceTypeById(ingredientId: string) {
		const ingredient = incorporationShares.find((item) => item.ingredientId === ingredientId);
		return ingredient ? ingredient.relevanceType : relevanceType.Irrelevant;
	}

	const months = useMemo(
		() => [
			t('months:JANUARY'),
			t('months:FEBRUARY'),
			t('months:MARCH'),
			t('months:APRIL'),
			t('months:MAY'),
			t('months:JUNE'),
			t('months:JULY'),
			t('months:AUGUST'),
			t('months:SEPTEMBER'),
			t('months:OCTOBER'),
			t('months:NOVEMBER'),
			t('months:DECEMBER'),
		],
		[t]
	);

	const currentMonthNumber = new Date().getMonth();
	const currentMonth = { key: currentMonthNumber + 1, name: months[currentMonthNumber] };

	const allIngredients: SegmentIngredient[] =
		segments?.flatMap((segment) => segment.ingredients) || [];
	const seasonIndependentIngredients: SegmentIngredient[] = [];
	const seasonalIngredients: SegmentIngredient[] = [];

	allIngredients.forEach((ing) => {
		const seasonality = seasonsPerIngredient.find((e) => e.ingredientId === ing.ingredientId);
		const monthAvailability = getMonthAvailability(seasonality?.getSeasonalityCode() || 0);

		if (
			monthAvailability.every((isAvailable) => !isAvailable) ||
			monthAvailability.every((isAvailable) => isAvailable)
		) {
			seasonIndependentIngredients.push(ing);
		} else {
			seasonalIngredients.push(ing);
		}
	});

	const [hoveredCol, setHoveredCol] = useState({ hoveredRange: [0, 0], color: '' });

	const isHovered = (index: number): boolean => {
		return hoveredCol.hoveredRange[0] <= index && index <= hoveredCol.hoveredRange[1];
	};

	const handleIngredientLinkClicked = (ingredientId: any) => {
		window.open(`/ingredient/detail/${ingredientId}`, '_blank');
	};

	const sparkle = (
		<FontAwesomeIcon
			style={{ fontSize: '10px', marginRight: '2px' }}
			icon={faSparkle}
		></FontAwesomeIcon>
	);

	const relevanceExplanation = (
		<span className={classes.explanationDiv}>
			<span className={classes.centeredFlex}>
				<span style={{ margin: '0 4px' }}>{t('seasonality:REQUIRED_SEASONALITY')}</span>
				{sparkle}
				{sparkle}
				{sparkle}
			</span>
			<span className={classes.centeredFlex}>
				| <span style={{ margin: '0 4px' }}>{t('seasonality:OPTIONAL_SEASONALITY')}</span>{' '}
				{sparkle}
				{sparkle}
			</span>
			<span className={classes.centeredFlex}>
				| <span style={{ margin: '0 4px' }}>{t('seasonality:IRRELEVANT_SEASONALITY')}</span>{' '}
				{sparkle}
			</span>
		</span>
	);

	const renderSeasonalityBar = (
		ingredientId: string | null,
		ingredientName: string | null,
		ingredientRelevance: string | null
	): JSX.Element => (
		<>
			<td
				onClick={() => handleIngredientLinkClicked(ingredientId)}
				style={{ transition: 'font-weight 0.2s ease' }}
			>
				{ingredientName}{' '}
				{ingredientRelevance === 'Optional' ? (
					<>
						{sparkle}
						{sparkle}
					</>
				) : ingredientRelevance === 'Required' ? (
					<>
						{sparkle}
						{sparkle}
						{sparkle}
					</>
				) : (
					sparkle
				)}
			</td>
			<SeasonalityBarBig
				seasonalityCode={
					seasonsPerIngredient
						.find((e) => e.ingredientId === ingredientId)
						?.getSeasonalityCode() || 0
				}
				currentMonthKey={currentMonth.key}
				showIfSeasonalityCodeIsZero={true}
			/>
		</>
	);

	const renderSeasonalIngredientsTable = (ingredients: SegmentIngredient[]) => (
		<table className="table table-sm table-bordered table-borderless table-striped-columns">
			<thead>
				<tr>
					<td style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>
						<div className={classes.seasonalityTableCell}>
							{t('recipe:SEASONAL_INGREDIENTS')}
						</div>
					</td>
					{months.map((month, index) => (
						<td
							key={month}
							className={
								month.includes(currentMonth.name) && !isHovered(index)
									? 'table-primary table-active'
									: ''
							}
							style={
								{
									textAlign: 'center',
									maxWidth: '1rem',
									backgroundColor:
										hoveredCol.hoveredRange[0] <= index &&
										index <= hoveredCol.hoveredRange[1]
											? hoveredCol.color
											: '',
									transition: 'background-color 0.2s ease',
									'--bs-table-bg-type': isHovered(index) ? 'initial' : '',
								} as React.CSSProperties
							}
						>
							{month.substring(0, 3)}
						</td>
					))}
				</tr>
			</thead>
			<tbody>
				{ingredients.map((ingredient, i) => (
					<tr key={i} className={`${classes.seasonalityTableRow}`}>
						{(ingredient.status.ingredient === 'Recognized' ||
							ingredient.status.ingredient === 'PartiallyRecognized') &&
							ingredient.ingredient &&
							renderSeasonalityBar(
								ingredient.ingredientId,
								ingredient.ingredient,
								getRelevanceTypeById(ingredient.ingredientId || '')
							)}
					</tr>
				))}
				<tr>
					<td></td>
				</tr>
				<SeasonsHistogramLarge
					currentMonth={currentMonth}
					seasonsHistogram={seasonsHistogram}
					setHoveredCol={setHoveredCol}
				></SeasonsHistogramLarge>
			</tbody>
		</table>
	);

	const renderSeasonIndependentIngredientsList = (ingredients: SegmentIngredient[]) => (
		<>
			<hr></hr>
			<p style={{ marginBottom: '1rem', marginTop: '1rem', fontWeight: 'bold' }}>
				{t('recipe:SEASON_INDEPENDENT_INGREDIENTS')}
			</p>
			<div className="row" style={{ maxWidth: '100%' }}>
				{ingredients.map(
					(ingredient, i) =>
						ingredient.ingredient && (
							<div key={i} className="col-2">
								<div
									className={`card ${classes.ingredientCard}`}
									style={{ marginBottom: '1rem' }}
									onClick={() =>
										handleIngredientLinkClicked(ingredient.ingredientId)
									}
								>
									<div className="card-body">
										<p className="card-text" style={{ fontSize: 'small' }}>
											<span style={{ marginRight: '4px' }}>
												{ingredient.ingredient}
											</span>
											{getRelevanceTypeById(ingredient.ingredientId || '') ===
											'Optional' ? (
												<>
													{sparkle}
													{sparkle}
												</>
											) : getRelevanceTypeById(
													ingredient.ingredientId || ''
											  ) === 'Required' ? (
												<>
													{sparkle}
													{sparkle}
													{sparkle}
												</>
											) : (
												sparkle
											)}
										</p>
									</div>
								</div>
							</div>
						)
				)}
			</div>
		</>
	);

	return (
		<div className="recipe-rendering">
			<div className="row">
				{seasonalIngredients.length > 0 || seasonIndependentIngredients.length > 0 ? (
					<div className="col-12">
						{relevanceExplanation}
						{seasonalIngredients.length === 0 ? (
							<p style={{ marginBottom: '1rem', fontWeight: 'bold' }}>
								{t('recipe:SEASONAL_INGREDIENTS_UNAVAILABLE')}
							</p>
						) : (
							renderSeasonalIngredientsTable(seasonalIngredients)
						)}

						{seasonIndependentIngredients.length === 0 ? (
							<p style={{ marginBottom: '1rem', fontWeight: 'bold' }}>
								{t('recipe:SEASONAL_INDEPENDENT_INGREDIENTS_UNAVAILABLE')}
							</p>
						) : (
							renderSeasonIndependentIngredientsList(seasonIndependentIngredients)
						)}
					</div>
				) : (
					<p>{t('_general:NO_ITEMS')}</p>
				)}
			</div>
		</div>
	);
};

export default SeasonalityView;
